import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import {
  Sparkles, Loader, RotateCcw, AlertTriangle, ChevronDown, ChevronRight,
  Users, MessageSquare, AlertOctagon,
  ListChecks, Target, Clock, History, ChevronLeft,
  Copy, Check,
} from 'lucide-react';

// ============================================================
// TEAM ANALYSIS CARD
//
// Lives on the Lead Performance page. Admin-only.
//
// Renders the AI Team Analysis as a history:
//   • Latest entry expanded at top (the 8-section structured output).
//   • Older entries listed below, collapsed by default, click to expand.
//   • Paginated: 5 entries per page.
//
// "Run AI Analysis" calls POST /stats/team-analysis. The backend
// decides whether to create a new entry (latest is older than the
// admin-configurable threshold, OR the lead count grew, OR the lead
// pool hash changed) or return the latest one unchanged. When it
// returns the latest unchanged we surface a small inline note so the
// admin knows nothing was actually re-run.
// ============================================================

const SEVERITY_META = {
  high:   { color: '#991B1B', bg: '#FEF2F2', border: '#FECACA', label: 'High' },
  medium: { color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', label: 'Medium' },
  low:    { color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB', label: 'Low' },
};

const HISTORY_PAGE_SIZE = 5;

const formatDateRange = (start, end) => {
  const fmt = (d) => new Date(d + 'T00:00:00Z').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
  return `${fmt(start)} – ${fmt(end)}`;
};

const formatTimestamp = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

// Short human delta like "4h ago" / "3 days ago". Used inline.
const formatAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

// Format a fractional-hours age (from the backend) into a human phrase
// for the reuse banner. The backend returns age_hours with 2 decimals;
// "0.01 hours ago" reads weird, so we collapse to minutes for sub-hour
// ages and keep the integer-hour rounding consistent above that.
const formatReuseAge = (hours) => {
  if (typeof hours !== 'number' || !Number.isFinite(hours)) return '';
  if (hours < 1 / 60) return 'less than a minute ago';
  if (hours < 1) {
    const mins = Math.max(1, Math.round(hours * 60));
    return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  }
  const rounded = Math.round(hours);
  return `${rounded} hour${rounded === 1 ? '' : 's'} ago`;
};

// ============================================================
// MARKDOWN EXPORT
//
// Renders one history entry's `result` JSON as clean markdown for
// clipboard paste. Header is one short metadata line (generated +
// range); the rest is the 8-section structured body. Every section
// heading is always rendered — empty arrays become "None.", an
// integer of 0 becomes "0", so structure stays consistent across
// copies.
// ============================================================
const SEVERITY_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };

const formatDateShort = (d) =>
  new Date(d + 'T00:00:00Z').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });

const formatGeneratedShort = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const buildAnalysisMarkdown = (entry, rangeStart, rangeEnd) => {
  const r = entry.result || {};
  const lines = [];

  // Header — one line of metadata so the copy is identifiable.
  // Prefer the entry's own range (set per row since history is now
  // cross-range) over the page's currently-selected range — the entry
  // may have been generated against a completely different window.
  const entryStart = entry.start_date || rangeStart;
  const entryEnd = entry.end_date || rangeEnd;
  lines.push(
    `_Generated ${formatGeneratedShort(entry.generated_at)} · Range: ${formatDateShort(entryStart)} – ${formatDateShort(entryEnd)}_`
  );
  lines.push('');

  // Team Summary
  lines.push('## Team Summary');
  lines.push(r.team_summary ? String(r.team_summary).trim() : 'None.');
  lines.push('');

  // Systemic Patterns
  lines.push('## Systemic Patterns');
  if (Array.isArray(r.systemic_patterns) && r.systemic_patterns.length > 0) {
    r.systemic_patterns.forEach((p) => {
      const sev = SEVERITY_LABEL[p.severity] || p.severity || 'Unknown';
      lines.push(`- **${p.pattern}** _(severity: ${sev})_`);
      if (p.evidence) lines.push(`  - ${p.evidence}`);
    });
  } else {
    lines.push('None.');
  }
  lines.push('');

  // Per-BDE Patterns
  lines.push('## Per-BDE Patterns');
  if (Array.isArray(r.per_bde_patterns) && r.per_bde_patterns.length > 0) {
    r.per_bde_patterns.forEach((b) => {
      lines.push(`### ${b.bde_name}`);
      lines.push('**Strengths:**');
      if (Array.isArray(b.strengths) && b.strengths.length > 0) {
        b.strengths.forEach((s) => lines.push(`- ${s}`));
      } else {
        lines.push('- None.');
      }
      lines.push('');
      lines.push('**What to change:**');
      if (Array.isArray(b.weaknesses) && b.weaknesses.length > 0) {
        b.weaknesses.forEach((w) => lines.push(`- ${w}`));
      } else {
        lines.push('- None.');
      }
      lines.push('');
      lines.push('**Examples (leads):**');
      if (Array.isArray(b.examples) && b.examples.length > 0) {
        lines.push(b.examples.join(', '));
      } else {
        lines.push('None.');
      }
      lines.push('');
    });
  } else {
    lines.push('None.');
    lines.push('');
  }

  // Common Stall Points
  lines.push('## Common Stall Points');
  if (Array.isArray(r.common_stall_points) && r.common_stall_points.length > 0) {
    r.common_stall_points.forEach((s) => {
      const c = typeof s.count === 'number' ? s.count : 0;
      lines.push(`- **${s.stage}** (${c} lead${c === 1 ? '' : 's'}): ${s.typical_cause || ''}`);
    });
  } else {
    lines.push('None.');
  }
  lines.push('');

  // Classification Health
  lines.push('## Classification Health');
  const ch = r.classification_health || {};
  lines.push(`- **Hot:** ${ch.hot_quality || 'None.'}`);
  lines.push(`- **Warm:** ${ch.warm_quality || 'None.'}`);
  lines.push(`- **Cold:** ${ch.cold_quality || 'None.'}`);
  lines.push(`- **Recommendation:** ${ch.recommendation || 'None.'}`);
  lines.push('');

  // Channel Insights (LinkedIn)
  lines.push('## Channel Insights (LinkedIn)');
  lines.push(r.channel_insights ? String(r.channel_insights).trim() : 'None.');
  lines.push('');

  // Data Quality Flags
  lines.push('## Data Quality Flags');
  const dqf = typeof r.data_quality_flags === 'number' ? r.data_quality_flags : 0;
  if (dqf > 0) {
    lines.push(`${dqf} lead${dqf === 1 ? '' : 's'} with mismatches between database fields and conversation.`);
  } else {
    lines.push('0 — no mismatches flagged.');
  }
  lines.push('');

  // Recommended Actions
  lines.push('## Recommended Actions');
  if (Array.isArray(r.recommended_actions) && r.recommended_actions.length > 0) {
    r.recommended_actions.forEach((a, i) => {
      lines.push(`${i + 1}. **${a.action}**`);
      if (a.target) lines.push(`   - Target: ${a.target}`);
      if (a.expected_impact) lines.push(`   - Expected impact: ${a.expected_impact}`);
    });
  } else {
    lines.push('None.');
  }

  return lines.join('\n');
};

// ============================================================
// COPY BUTTON — per-entry. Holds its own "copied" state so clicks
// on different entries don't interfere with each other.
// ============================================================
const CopyButton = ({ entry, rangeStart, rangeEnd }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      const md = buildAnalysisMarkdown(entry, rangeStart, rangeEnd);
      await navigator.clipboard.writeText(md);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert('Could not copy to clipboard. Select and copy manually.');
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="btn btn-secondary btn-sm"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        flexShrink: 0,
      }}
      title="Copy this analysis as markdown"
    >
      {copied
        ? <><Check size={12} /> Copied</>
        : <><Copy size={12} /> Copy</>}
    </button>
  );
};

// ============================================================
// COLLAPSIBLE SECTION (within an analysis detail)
// ============================================================
const Section = ({ title, icon: Icon, defaultOpen = true, accentColor, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      marginTop: 16, borderTop: '1px solid #E5E7EB', paddingTop: 16,
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: 0, display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 14, fontWeight: 600, color: accentColor || '#111827',
          textTransform: 'uppercase', letterSpacing: '0.05em',
        }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {Icon && <Icon size={14} />}
        {title}
      </button>
      {open && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
  );
};

const PatternCard = ({ p }) => {
  const meta = SEVERITY_META[p.severity] || SEVERITY_META.low;
  return (
    <div style={{
      padding: 12, background: meta.bg,
      border: `1px solid ${meta.border}`,
      borderLeft: `4px solid ${meta.color}`,
      borderRadius: 6, marginBottom: 8,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 8, marginBottom: 6,
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', flex: 1 }}>
          {p.pattern}
        </div>
        <div style={{
          fontSize: 10, fontWeight: 600,
          padding: '2px 8px', borderRadius: 999,
          background: meta.color, color: '#FFFFFF',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          flexShrink: 0,
        }}>{meta.label}</div>
      </div>
      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
        {p.evidence}
      </div>
    </div>
  );
};

const BdeAccordion = ({ entry, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasStrengths = entry.strengths && entry.strengths.length > 0;
  const hasWeaknesses = entry.weaknesses && entry.weaknesses.length > 0;
  return (
    <div style={{
      border: '1px solid #E5E7EB', borderRadius: 6, marginBottom: 8,
      overflow: 'hidden',
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', background: '#F9FAFB', border: 'none',
          padding: '10px 12px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 14, color: '#111827', textAlign: 'left',
        }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Users size={14} color="#6B7280" />
        <span style={{ fontWeight: 600 }}>{entry.bde_name}</span>
        <span style={{ fontSize: 12, color: '#6B7280', marginLeft: 'auto' }}>
          {(entry.strengths?.length || 0)} strengths · {(entry.weaknesses?.length || 0)} to fix
        </span>
      </button>
      {open && (
        <div style={{ padding: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
          <div>
            <div style={{
              fontSize: 12, fontWeight: 600, color: '#065F46', marginBottom: 6,
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>Strengths</div>
            {hasStrengths ? (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#111827' }}>
                {entry.strengths.map((s, i) => (
                  <li key={i} style={{ marginBottom: 4, lineHeight: 1.5 }}>{s}</li>
                ))}
              </ul>
            ) : <div style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>Nothing notable.</div>}
          </div>
          <div>
            <div style={{
              fontSize: 12, fontWeight: 600, color: '#991B1B', marginBottom: 6,
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>What to change</div>
            {hasWeaknesses ? (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#111827' }}>
                {entry.weaknesses.map((w, i) => (
                  <li key={i} style={{ marginBottom: 4, lineHeight: 1.5 }}>{w}</li>
                ))}
              </ul>
            ) : <div style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>Nothing flagged.</div>}
          </div>
          {entry.examples && entry.examples.length > 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{
                fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6,
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>Examples (leads)</div>
              <div style={{ fontSize: 13, color: '#374151' }}>
                {entry.examples.join(', ')}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// ANALYSIS DETAIL — the 8-section structured output. Renders the
// `result` JSON from one ai_team_analyses row. Used both for the
// latest entry (always expanded) and for older entries (expanded
// on click).
// ============================================================
const AnalysisDetail = ({ result }) => (
  <>
    {/* Team summary — always visible, no collapse */}
    <div style={{
      marginTop: 16, padding: 12,
      background: '#EFF6FF', border: '1px solid #BFDBFE',
      borderLeft: '4px solid #3B82F6', borderRadius: 6,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: '#1D4ED8',
        textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
      }}>Team Summary</div>
      <div style={{ fontSize: 14, color: '#111827', lineHeight: 1.55 }}>
        {result.team_summary}
      </div>
    </div>

    {Array.isArray(result.recommended_actions) && result.recommended_actions.length > 0 && (
      <Section title="Recommended actions" icon={Target} accentColor="#1D4ED8" defaultOpen>
        <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
          {result.recommended_actions.map((a, i) => (
            <li key={i} style={{
              display: 'grid',
              gridTemplateColumns: '28px 1fr',
              gap: 10, padding: '10px 12px',
              background: '#EFF6FF', border: '1px solid #BFDBFE',
              borderLeft: '4px solid #3B82F6',
              borderRadius: 6, marginBottom: 8,
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: 11,
                background: '#3B82F6', color: '#FFFFFF',
                fontSize: 12, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginTop: 1,
              }}>{i + 1}</div>
              <div>
                <div style={{ fontSize: 14, color: '#111827', fontWeight: 500, lineHeight: 1.4 }}>
                  {a.action}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                  <strong>Target:</strong> {a.target}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                  <strong>Expected impact:</strong> {a.expected_impact}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </Section>
    )}

    {Array.isArray(result.systemic_patterns) && result.systemic_patterns.length > 0 && (
      <Section title="Systemic patterns" icon={AlertOctagon} accentColor="#991B1B" defaultOpen>
        {result.systemic_patterns.map((p, i) => <PatternCard key={i} p={p} />)}
      </Section>
    )}

    {Array.isArray(result.per_bde_patterns) && result.per_bde_patterns.length > 0 && (
      <Section title="Per-BDE patterns" icon={Users} accentColor="#6B7280">
        {result.per_bde_patterns.map((entry) => (
          <BdeAccordion key={entry.bde_name} entry={entry} />
        ))}
      </Section>
    )}

    {Array.isArray(result.common_stall_points) && result.common_stall_points.length > 0 && (
      <Section title="Common stall points" icon={AlertTriangle} accentColor="#92400E">
        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 12, color: '#6B7280', fontWeight: 600 }}>Stage</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, color: '#6B7280', fontWeight: 600 }}>Count</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 12, color: '#6B7280', fontWeight: 600 }}>Typical cause</th>
              </tr>
            </thead>
            <tbody>
              {result.common_stall_points.map((s, i) => (
                <tr key={i} style={{ borderTop: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '8px 12px', fontSize: 14, color: '#111827' }}>{s.stage}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 14, color: '#991B1B', fontWeight: 600 }}>{s.count}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: '#374151' }}>{s.typical_cause}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    )}

    {result.classification_health && (
      <Section title="Classification health" icon={ListChecks} accentColor="#6B7280">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div style={{ padding: 12, background: '#FEF2F2', border: '1px solid #FECACA', borderLeft: '4px solid #DC2626', borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#DC2626', textTransform: 'uppercase', marginBottom: 4 }}>Hot</div>
            <div style={{ fontSize: 13, color: '#111827', lineHeight: 1.5 }}>{result.classification_health.hot_quality}</div>
          </div>
          <div style={{ padding: 12, background: '#FFFBEB', border: '1px solid #FDE68A', borderLeft: '4px solid #F59E0B', borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#F59E0B', textTransform: 'uppercase', marginBottom: 4 }}>Warm</div>
            <div style={{ fontSize: 13, color: '#111827', lineHeight: 1.5 }}>{result.classification_health.warm_quality}</div>
          </div>
          <div style={{ padding: 12, background: '#EFF6FF', border: '1px solid #BFDBFE', borderLeft: '4px solid #2563EB', borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#2563EB', textTransform: 'uppercase', marginBottom: 4 }}>Cold</div>
            <div style={{ fontSize: 13, color: '#111827', lineHeight: 1.5 }}>{result.classification_health.cold_quality}</div>
          </div>
        </div>
        <div style={{
          marginTop: 12, padding: 12,
          background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>
            Recommendation
          </div>
          <div style={{ fontSize: 13, color: '#111827' }}>{result.classification_health.recommendation}</div>
        </div>
      </Section>
    )}

    {result.channel_insights && (
      <Section title="Channel insights (LinkedIn)" icon={MessageSquare} accentColor="#6B7280">
        <div style={{ fontSize: 14, color: '#111827', lineHeight: 1.55 }}>
          {result.channel_insights}
        </div>
      </Section>
    )}

    {typeof result.data_quality_flags === 'number' && (
      <Section title="Data quality" icon={AlertTriangle} accentColor="#6B7280">
        <div style={{ fontSize: 14, color: '#111827' }}>
          {result.data_quality_flags > 0 ? (
            <>
              <strong style={{ color: '#991B1B' }}>{result.data_quality_flags}</strong> lead{result.data_quality_flags === 1 ? '' : 's'} in this range
              {' '}have a mismatch between the database fields and the conversation.
              Run the per-lead AI Diagnostic on those leads to see exactly what's off.
            </>
          ) : (
            <>No data-quality mismatches flagged in this period.</>
          )}
        </div>
      </Section>
    )}
  </>
);

// ============================================================
// ENTRY METADATA — the "Generated X · by Y · model Z · N leads"
// strip rendered at the top of each entry card.
// ============================================================
const EntryMeta = ({ entry }) => {
  const parts = [];
  if (entry.generated_at) {
    parts.push(`Generated ${formatTimestamp(entry.generated_at)} (${formatAgo(entry.generated_at)})`);
  }
  if (entry.generated_by_name) parts.push(`by ${entry.generated_by_name}`);
  if (entry.model_used) parts.push(`model: ${entry.model_used}`);
  if (typeof entry.lead_count === 'number') {
    const sampledNote = entry.sampled_count && entry.sampled_count < entry.lead_count
      ? `${entry.sampled_count} of ${entry.lead_count} sampled`
      : `${entry.lead_count} lead${entry.lead_count === 1 ? '' : 's'}`;
    parts.push(sampledNote);
  }
  return (
    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
      {parts.join(' · ')}
    </div>
  );
};

// ============================================================
// HISTORY ENTRY CARD — collapsed by default, expand to show the
// full AnalysisDetail. Used for older entries only.
// ============================================================
const HistoryEntryCard = ({ entry, rangeStart, rangeEnd }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden',
      marginBottom: 8,
    }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          background: '#F9FAFB',
          padding: '10px 14px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10,
        }}
      >
        {open ? <ChevronDown size={16} color="#6B7280" /> : <ChevronRight size={16} color="#6B7280" />}
        <Clock size={14} color="#6B7280" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, color: '#111827', fontWeight: 500 }}>
            {formatTimestamp(entry.generated_at)}
          </div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>
            {[
              entry.start_date && entry.end_date && formatDateRange(entry.start_date, entry.end_date),
              entry.generated_by_name && `by ${entry.generated_by_name}`,
              entry.model_used,
              typeof entry.lead_count === 'number' && `${entry.lead_count} lead${entry.lead_count === 1 ? '' : 's'}`,
            ].filter(Boolean).join(' · ')}
          </div>
        </div>
        <CopyButton entry={entry} rangeStart={rangeStart} rangeEnd={rangeEnd} />
      </div>
      {open && entry.result && (
        <div style={{ padding: '0 14px 14px' }}>
          <AnalysisDetail result={entry.result} />
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN
// ============================================================
const TeamAnalysisCard = ({ rangeStart, rangeEnd }) => {
  const [latest, setLatest] = useState(null);
  const [currentLeadCount, setCurrentLeadCount] = useState(0);
  const [leadsAddedSince, setLeadsAddedSince] = useState(0);
  const [poolChanged, setPoolChanged] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  // "We didn't actually re-run because the latest was X hours old and
  // nothing changed." Set after a POST that returned created_new=false.
  // Cleared on next regenerate, range change, or after a few seconds.
  const [reuseHint, setReuseHint] = useState(null);

  const fetchLatest = useCallback(async () => {
    const res = await api.get('/stats/team-analysis', { params: { start: rangeStart, end: rangeEnd } });
    setLatest(res.data.latest);
    setCurrentLeadCount(res.data.current_lead_count ?? 0);
    setLeadsAddedSince(res.data.leads_added_since ?? 0);
    setPoolChanged(!!res.data.pool_changed);
  }, [rangeStart, rangeEnd]);

  const fetchHistory = useCallback(async (page) => {
    setHistoryLoading(true);
    try {
      // History is cross-range — same list regardless of which date
      // range the user has selected. Range params intentionally omitted.
      const res = await api.get('/stats/team-analysis/history', {
        params: { page, page_size: HISTORY_PAGE_SIZE },
      });
      setHistory(res.data.entries || []);
      setHistoryTotal(res.data.total || 0);
      setHistoryTotalPages(res.data.total_pages || 1);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Reload both endpoints on mount and whenever the range changes.
  useEffect(() => {
    if (!rangeStart || !rangeEnd) return;
    setLoading(true);
    setError('');
    setReuseHint(null);
    setHistoryPage(1);
    Promise.all([fetchLatest(), fetchHistory(1)])
      .catch((e) => setError(e.response?.data?.error || 'Failed to load team analysis.'))
      .finally(() => setLoading(false));
  }, [rangeStart, rangeEnd, fetchLatest, fetchHistory]);

  // History pagination — only fires after the first load. The mount
  // effect above also calls fetchHistory(1), so skip when page is 1
  // to avoid the double-fetch on initial render.
  useEffect(() => {
    if (loading) return;
    fetchHistory(historyPage).catch(() => {});
  }, [historyPage, fetchHistory, loading]);

  const handleRun = async () => {
    if (running) return;
    setRunning(true);
    setError('');
    setReuseHint(null);
    try {
      // Body must be a JSON object (not null) — Express body-parser strict
      // mode rejects top-level null with a 400 before our handler runs.
      const res = await api.post('/stats/team-analysis', {}, {
        params: { start: rangeStart, end: rangeEnd },
        timeout: 120000, // Anthropic call can be 30-60s; default axios is 10s
      });
      setLatest(res.data.latest);
      setCurrentLeadCount(res.data.current_lead_count ?? 0);
      if (res.data.created_new) {
        // New entry was inserted — push the previous latest into history
        // by refetching page 1 (cheaper than reasoning about insert order
        // client-side, and surfaces any new entries another admin made
        // in parallel).
        setHistoryPage(1);
        await fetchHistory(1);
        // Recompute staleness signals against the new latest.
        setLeadsAddedSince(0);
        setPoolChanged(false);
      } else {
        // Latest is still the same row — show the silent-surface note.
        setReuseHint({
          threshold_hours: res.data.reuse_reason?.threshold_hours,
          age_hours: res.data.reuse_reason?.age_hours,
        });
      }
    } catch (e) {
      setError(e.response?.data?.error || 'AI analysis failed. Try again in a moment.');
    } finally {
      setRunning(false);
    }
  };

  const subtitle = (() => {
    if (loading) return 'Loading…';
    if (running) {
      return `Analyzing ${currentLeadCount} leads in ${formatDateRange(rangeStart, rangeEnd)}. This usually takes 30-60 seconds — stay on this page.`;
    }
    if (latest) {
      return `${formatDateRange(rangeStart, rangeEnd)} · ${currentLeadCount} lead${currentLeadCount === 1 ? '' : 's'} in range`;
    }
    if (currentLeadCount === 0) {
      return `No leads in this date range — nothing to analyze.`;
    }
    return `${currentLeadCount} leads in ${formatDateRange(rangeStart, rangeEnd)} · no analysis yet.`;
  })();

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sparkles size={18} color="#3B82F6" /> AI Team Analysis
          </h3>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>{subtitle}</div>
        </div>
        {currentLeadCount > 0 && (
          <button
            onClick={handleRun}
            disabled={running || loading}
            className={latest ? 'btn btn-secondary' : 'btn btn-primary'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              opacity: (running || loading) ? 0.6 : 1,
              cursor: (running || loading) ? 'default' : 'pointer',
              flexShrink: 0,
            }}
          >
            {running
              ? <><Loader size={14} /> Analyzing…</>
              : latest
                ? <><RotateCcw size={14} /> Run AI Analysis</>
                : <><Sparkles size={14} /> Run AI Analysis</>}
          </button>
        )}
      </div>

      {/* Staleness banner — page-load read. Tells the admin "this is
          how old the latest is and what changed since" without doing
          anything automatic. */}
      {latest && !loading && !running && (leadsAddedSince > 0 || poolChanged) && (
        <div style={{
          marginTop: 12, padding: 10, background: '#FFFBEB',
          border: '1px solid #FDE68A', borderRadius: 6,
          fontSize: 13, color: '#92400E',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertTriangle size={14} />
          <span>
            {leadsAddedSince > 0
              ? `${leadsAddedSince} new lead${leadsAddedSince === 1 ? '' : 's'} added since the latest analysis.`
              : 'Leads in this range have been edited since the latest analysis.'}
            {' '}Click <strong>Run AI Analysis</strong> to refresh.
          </span>
        </div>
      )}

      {/* Reuse hint — shown after a POST that returned created_new=false.
          The backend tells us how old the latest is and what the threshold
          is so we can render exactly what it decided. */}
      {reuseHint && !running && (
        <div style={{
          marginTop: 12, padding: 10, background: '#EFF6FF',
          border: '1px solid #BFDBFE', borderRadius: 6,
          fontSize: 13, color: '#1D4ED8',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Clock size={14} />
          <span>
            Showing the latest analysis from {formatReuseAge(reuseHint.age_hours)} — no new leads since,
            and the threshold is {reuseHint.threshold_hours} hour{reuseHint.threshold_hours === 1 ? '' : 's'}.
          </span>
        </div>
      )}

      {error && (
        <div style={{
          marginTop: 12, padding: 8, background: '#FEE2E2',
          color: '#991B1B', borderRadius: 6, fontSize: 13,
        }}>{error}</div>
      )}

      {/* LATEST ENTRY — expanded by default */}
      {latest && latest.result && (
        <div style={{ marginTop: 12 }}>
          <div style={{
            padding: '8px 12px', background: '#F0FDF4',
            border: '1px solid #BBF7D0', borderLeft: '4px solid #16A34A',
            borderRadius: 6,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>
                Latest analysis
              </div>
              <EntryMeta entry={latest} />
            </div>
            <CopyButton entry={latest} rangeStart={rangeStart} rangeEnd={rangeEnd} />
          </div>
          <AnalysisDetail result={latest.result} />
        </div>
      )}

      {/* HISTORY SECTION — older entries, collapsed by default. Now
          cross-range: the list includes every analysis ever run, not
          just ones whose (start_date, end_date) match the page's
          currently-selected range. We dedupe the entry that's already
          rendered above as "Latest analysis" so it doesn't appear
          twice — done here on the client because the backend
          intentionally does NOT filter (the "latest for current
          range" is a frontend concept; the API returns all rows). */}
      {(() => {
        const visibleHistory = latest
          ? history.filter((e) => e.id !== latest.id)
          : history;
        // historyTotal is the full cross-range count from the backend
        // (latest entry NOT excluded). When latest is set, subtract 1
        // — the latest is rendered separately above and should not be
        // counted in "Previous". This is uniform across pages: on the
        // page that contains the latest, visibleHistory has one fewer
        // row; on other pages, displayTotal still reflects the true
        // count of non-latest entries.
        const displayTotal = latest
          ? Math.max(0, historyTotal - 1)
          : historyTotal;
        if (displayTotal === 0) return null;
        return (
          <div style={{ marginTop: 24, borderTop: '1px solid #E5E7EB', paddingTop: 16 }}>
            <div style={{
              fontSize: 14, fontWeight: 600, color: '#111827',
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
            }}>
              <History size={16} color="#6B7280" /> Previous analyses ({displayTotal})
            </div>

            {visibleHistory.map((entry) => (
              <HistoryEntryCard
                key={entry.id}
                entry={entry}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
              />
            ))}

          {historyLoading && (
            <div style={{ fontSize: 13, color: '#6B7280', padding: '8px 4px' }}>
              Loading…
            </div>
          )}

          {/* Pagination — only renders when there's more than one page */}
          {historyTotalPages > 1 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              marginTop: 12, fontSize: 13, color: '#6B7280',
            }}>
              <button
                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                disabled={historyPage <= 1 || historyLoading}
                className="btn btn-secondary btn-sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span>Page {historyPage} of {historyTotalPages}</span>
              <button
                onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                disabled={historyPage >= historyTotalPages || historyLoading}
                className="btn btn-secondary btn-sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
        );
      })()}
    </div>
  );
};

export default TeamAnalysisCard;
